/**
 * Estado de la conexión y de la sincronización.
 *
 * Es lo que hace visible algo que, si no, sería invisible y desconcertante: que has
 * registrado un gasto y **todavía no ha llegado al servidor**. Un usuario que no lo
 * sabe piensa que la aplicación ha fallado; uno que lo ve, confía.
 *
 * Se sincroniza al arrancar, al recuperar la conexión, al volver a la pestaña y
 * cada minuto. Nada de esto bloquea la pantalla: si falla, se anota y se reintenta.
 */

import { create } from 'zustand';

import { pendingCount } from './outbox';
import { readLastSync } from './outbox';
import { synchronize, type SyncOutcome } from './sync';

/** Cada cuánto se reintenta, en milisegundos. */
const INTERVAL_MS = 60_000;

interface OfflineState {
  /** El navegador dice que hay conexión. */
  online: boolean;
  /** Hay una sincronización en curso. */
  syncing: boolean;
  /** Cuántas operaciones esperan en la cola. */
  pending: number;
  /** Cuándo se sincronizó por última vez. */
  lastSync: string | null;
  /** Si el almacén local está disponible. */
  storageReady: boolean;
  /** Último error, si lo hubo. */
  lastError: string | null;
  /** Operaciones que el servidor rechazó, con su motivo. */
  rejected: Array<{ id: string; error: string }>;
  /** Resultado de la última sincronización, para poder avisar. */
  lastOutcome: SyncOutcome | null;

  init: () => Promise<void>;
  refreshPending: () => Promise<void>;
  sync: () => Promise<SyncOutcome>;
  stop: () => void;
}

let timer: number | null = null;

export const useOffline = create<OfflineState>((set, get) => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  pending: 0,
  lastSync: null,
  storageReady: false,
  lastError: null,
  rejected: [],
  lastOutcome: null,

  /** Prepara el estado y arranca la sincronización periódica. */
  init: async () => {
    // ¿Se puede guardar sin conexión en este navegador?
    try {
      const { openDatabase } = await import('./db');
      await openDatabase();
      set({ storageReady: true });
    } catch {
      // Sin almacén local la aplicación funciona, pero solo con conexión.
      set({ storageReady: false });
      return;
    }

    set({ online: navigator.onLine, lastSync: await readLastSync() });
    await get().refreshPending();

    // Al volver la conexión, se sincroniza de inmediato.
    const onOnline = (): void => {
      set({ online: true });
      void get().sync();
    };
    const onOffline = (): void => set({ online: false });

    // Al volver a la pestaña, se sincroniza: puede llevar horas en segundo plano.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && get().online) void get().sync();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);

    void get().sync();
    timer = window.setInterval(() => {
      if (get().online) void get().sync();
    }, INTERVAL_MS);
  },

  /** Relee cuántas cosas esperan en la cola. */
  refreshPending: async () => {
    try {
      set({ pending: await pendingCount() });
    } catch {
      set({ pending: 0 });
    }
  },

  /** Hace una pasada completa: envía la cola y trae los cambios. */
  sync: async () => {
    if (get().syncing) {
      return (
        get().lastOutcome ?? { pushed: 0, failed: 0, pulled: 0, rejected: [] }
      );
    }

    set({ syncing: true });

    try {
      const outcome = await synchronize();
      set({
        lastOutcome: outcome,
        lastError: outcome.error ?? null,
        rejected: outcome.rejected,
        lastSync: (await readLastSync()) ?? null,
      });
      await get().refreshPending();
      return outcome;
    } finally {
      set({ syncing: false });
    }
  },

  /** Detiene la sincronización periódica. */
  stop: () => {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  },
}));

/**
 * Descripción en palabras de lo que está pasando, para la barra de estado.
 * Se escribe pensando en alguien que no sabe qué es sincronizar.
 */
export function connectionLabel(state: {
  online: boolean;
  syncing: boolean;
  pending: number;
  storageReady: boolean;
}): { text: string; tone: 'ok' | 'busy' | 'warn' } {
  if (!state.online) {
    return state.pending > 0
      ? {
          text: `Sin conexión. ${state.pending} ${state.pending === 1 ? 'movimiento guardado' : 'movimientos guardados'} en el teléfono, se enviarán al volver la red.`,
          tone: 'warn',
        }
      : { text: 'Sin conexión. Puedes seguir registrando.', tone: 'warn' };
  }

  if (state.syncing) return { text: 'Sincronizando…', tone: 'busy' };

  if (state.pending > 0) {
    return {
      text: `${state.pending} ${state.pending === 1 ? 'movimiento' : 'movimientos'} por enviar.`,
      tone: 'busy',
    };
  }

  if (!state.storageReady) {
    return { text: 'Este navegador no guarda sin conexión.', tone: 'warn' };
  }

  return { text: 'Todo guardado', tone: 'ok' };
}
